import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

@observer
class DealersSection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            editingDealer: null,
            newDealerPrefix: null,
            linkingPrefix: null,
            creatingNewDealer: false,
        };
    }

    loadPrefixes = async () => {
        const { dealerStore } = this.props.stores;
        await dealerStore.loadPrefixes();
    }

    onCreateDealer = async (prefix) => {
        this.setState({ newDealerPrefix: prefix, editingDealer: null, linkingPrefix: null, creatingNewDealer: false });
    }

    onCreateNewDealer = () => {
        this.setState({ creatingNewDealer: true, editingDealer: null, newDealerPrefix: null, linkingPrefix: null });
    }

    onEditDealer = (dealer) => {
        this.setState({ editingDealer: dealer, newDealerPrefix: null, linkingPrefix: null, creatingNewDealer: false });
    }

    onLinkPrefix = (prefix) => {
        this.setState({ linkingPrefix: prefix, editingDealer: null, newDealerPrefix: null, creatingNewDealer: false });
    }

    onCancelEdit = () => {
        this.setState({ editingDealer: null, newDealerPrefix: null, linkingPrefix: null, creatingNewDealer: false });
    }

    onSaveDealer = async (e) => {
        e.preventDefault();
        const { dealerStore, productStore } = this.props.stores;
        const { editingDealer, newDealerPrefix } = this.state;

        const formData = new FormData(e.target);
        const dealerData = {
            name: formData.get('name'),
            prefix: formData.get('prefix'),
            address: formData.get('address'),
            postalcode: formData.get('postalcode'),
            place: formData.get('place'),
            phone: formData.get('phone'),
            email: formData.get('email'),
            website: formData.get('website'),
            contactPerson: formData.get('contactPerson'),
            orgNumber: formData.get('orgNumber'),
        };

        if (editingDealer && editingDealer.id) {
            // Update existing dealer
            await util.fetchApi(`/api/dealers/${editingDealer.id}`, { method: 'PATCH' }, dealerData);
        } else {
            // Create new dealer
            const response = await util.fetchApi('/api/dealers/', { method: 'POST' }, dealerData);
            // Only link products if prefix is provided
            if (response && response.status === 201 && response.data && response.data.dealer && dealerData.prefix) {
                await dealerStore.linkDealerToPrefix(dealerData.prefix, response.data.dealer.id);
            }
        }

        this.setState({ editingDealer: null, newDealerPrefix: null, creatingNewDealer: false });
        await this.loadPrefixes();

        // Update productStore dealers for edit product dropdown
        const { dealers } = dealerStore;
        productStore.updateKeyValue('dealers', dealers);
    }

    onLinkDealerToPrefix = async (prefix, dealerId) => {
        const { dealerStore } = this.props.stores;
        await dealerStore.linkDealerToPrefix(prefix, dealerId);
        this.setState({ linkingPrefix: null });
    }

    onUnlinkDealer = async (prefix) => {
        const { dealerStore } = this.props.stores;
        await dealerStore.linkDealerToPrefix(prefix, 0);
    }

    render() {
        const { editingDealer, newDealerPrefix, linkingPrefix, creatingNewDealer } = this.state;
        const { userStore, dealerStore } = this.props.stores;
        const { buttonOnly, contentOnly, showDealers, onToggleDealers } = this.props;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const { prefixes = [], dealers = [] } = dealerStore;
        const showForm = editingDealer || newDealerPrefix || creatingNewDealer;

        // Render only the toggle button (for inline placement)
        if (buttonOnly) {
            return (
                <button
                    type='button'
                    class={`btn btn-sm ${showDealers ? 'btn-primary' : 'btn-outline-primary'} rounded-pill`}
                    onClick={onToggleDealers}
                >
                    <i class='fa-solid fa-building me-1' />
                    <Text id='product.dealers'>Dealers / Suppliers</Text>
                    <i class={`fa-solid fa-chevron-${showDealers ? 'up' : 'down'} ms-2`} />
                </button>
            );
        }

        // Render only the content (when button is elsewhere)
        if (contentOnly) {
            if (!showDealers) return null;
            return (
                    <div class={`d-flex flex-column rounded-lg mx-3 mb-3 p-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <div class='d-flex justify-content-between align-items-center mb-3'>
                            <h5 class='mb-0'>
                                <i class='fa-solid fa-building me-2' />
                                <Text id='product.dealers-prefixes'>Product Prefixes & Dealers</Text>
                            </h5>
                            <div class='d-flex gap-2'>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-success rounded-pill'
                                    onClick={this.onCreateNewDealer}
                                >
                                    <i class='fa-solid fa-plus me-1' />
                                    <Text id='product.new-dealer'>New Dealer</Text>
                                </button>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-primary rounded-pill'
                                    onClick={() => this.loadPrefixes()}
                                >
                                    <i class='fa-solid fa-refresh me-1' />
                                    <Text id='common.refresh'>Refresh</Text>
                                </button>
                            </div>
                        </div>

                        {/* Dealer Form (for editing or creating) */}
                        {showForm && (
                            <div class={`mb-3 p-3 border rounded ${darkmode ? 'border-secondary' : ''}`}>
                                <h6 class='mb-3'>
                                    {editingDealer ? (
                                        <><i class='fa-solid fa-edit me-2' /><Text id='product.edit-dealer'>Edit Dealer</Text>: {editingDealer.name}</>
                                    ) : newDealerPrefix ? (
                                        <><i class='fa-solid fa-plus me-2' /><Text id='product.create-dealer'>Create Dealer for prefix "{newDealerPrefix}"</Text></>
                                    ) : (
                                        <><i class='fa-solid fa-plus me-2' /><Text id='product.create-new-dealer'>Create New Dealer</Text></>
                                    )}
                                </h6>
                                <form key={`dealer-form-${newDealerPrefix || editingDealer?.id || 'new'}`} onSubmit={this.onSaveDealer}>
                                    <div class='row g-2'>
                                        <div class='col-md-6'>
                                            <label class='form-label small'><Text id='product.dealer-name'>Company Name</Text> *</label>
                                            <input
                                                type='text'
                                                name='name'
                                                class='form-control form-control-sm'
                                                defaultValue={editingDealer?.name || ''}
                                                required
                                            />
                                        </div>
                                        <div class='col-md-3'>
                                            <label class='form-label small'><Text id='product.dealer-prefix'>Prefix</Text></label>
                                            <input
                                                type='text'
                                                name='prefix'
                                                class='form-control form-control-sm'
                                                defaultValue={editingDealer?.prefix || newDealerPrefix || ''}
                                                readOnly={!!newDealerPrefix}
                                            />
                                        </div>
                                        <div class='col-md-3'>
                                            <label class='form-label small'><Text id='product.dealer-org'>Org Number</Text></label>
                                            <input
                                                type='text'
                                                name='orgNumber'
                                                class='form-control form-control-sm'
                                                defaultValue={editingDealer?.orgNumber || ''}
                                            />
                                        </div>
                                        <div class='col-md-6'>
                                            <label class='form-label small'><Text id='product.dealer-address'>Address</Text></label>
                                            <input
                                                type='text'
                                                name='address'
                                                class='form-control form-control-sm'
                                                defaultValue={editingDealer?.address || ''}
                                            />
                                        </div>
                                        <div class='col-md-3'>
                                            <label class='form-label small'><Text id='product.dealer-postalcode'>Postal Code</Text></label>
                                            <input
                                                type='text'
                                                name='postalcode'
                                                class='form-control form-control-sm'
                                                defaultValue={editingDealer?.postalcode || ''}
                                            />
                                        </div>
                                        <div class='col-md-3'>
                                            <label class='form-label small'><Text id='product.dealer-place'>Place</Text></label>
                                            <input
                                                type='text'
                                                name='place'
                                                class='form-control form-control-sm'
                                                defaultValue={editingDealer?.place || ''}
                                            />
                                        </div>
                                        <div class='col-md-4'>
                                            <label class='form-label small'><Text id='product.dealer-phone'>Phone</Text></label>
                                            <input
                                                type='text'
                                                name='phone'
                                                class='form-control form-control-sm'
                                                defaultValue={editingDealer?.phone || ''}
                                            />
                                        </div>
                                        <div class='col-md-4'>
                                            <label class='form-label small'><Text id='product.dealer-email'>Email</Text></label>
                                            <input
                                                type='email'
                                                name='email'
                                                class='form-control form-control-sm'
                                                defaultValue={editingDealer?.email || ''}
                                            />
                                        </div>
                                        <div class='col-md-4'>
                                            <label class='form-label small'><Text id='product.dealer-website'>Website</Text></label>
                                            <input
                                                type='text'
                                                name='website'
                                                class='form-control form-control-sm'
                                                defaultValue={editingDealer?.website || ''}
                                            />
                                        </div>
                                        <div class='col-md-6'>
                                            <label class='form-label small'><Text id='product.dealer-contact'>Contact Person</Text></label>
                                            <input
                                                type='text'
                                                name='contactPerson'
                                                class='form-control form-control-sm'
                                                defaultValue={editingDealer?.contactPerson || ''}
                                            />
                                        </div>
                                    </div>
                                    <div class='mt-3 d-flex gap-2'>
                                        <button type='submit' class='btn btn-sm btn-primary'>
                                            <i class='fa-solid fa-save me-1' />
                                            <Text id='common.save'>Save</Text>
                                        </button>
                                        <button type='button' class='btn btn-sm btn-outline-secondary' onClick={this.onCancelEdit}>
                                            <Text id='common.cancel'>Cancel</Text>
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Link to existing dealer dropdown */}
                        {linkingPrefix && (
                            <div class={`mb-3 p-3 border rounded ${darkmode ? 'border-secondary' : ''}`}>
                                <h6 class='mb-3'>
                                    <i class='fa-solid fa-link me-2' />
                                    <Text id='product.link-prefix'>Link prefix "{linkingPrefix}" to existing dealer</Text>
                                </h6>
                                <div class='d-flex gap-2 align-items-center'>
                                    <select
                                        class='form-select form-select-sm'
                                        style='max-width: 300px;'
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                this.onLinkDealerToPrefix(linkingPrefix, parseInt(e.target.value, 10));
                                            }
                                        }}
                                    >
                                        <option value=''>-- Select Dealer --</option>
                                        {dealers.map(d => (
                                            <option key={d.id} value={d.id}>{d.name} {d.prefix ? `(${d.prefix})` : ''}</option>
                                        ))}
                                    </select>
                                    <button type='button' class='btn btn-sm btn-outline-secondary' onClick={this.onCancelEdit}>
                                        <Text id='common.cancel'>Cancel</Text>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Prefixes Table */}
                        <div class='table-responsive'>
                            <table class={`table table-sm table-striped ${darkmode ? 'table-dark' : ''}`}>
                                <thead>
                                    <tr>
                                        <th style='width: 100px;'><Text id='product.prefix'>Prefix</Text></th>
                                        <th style='width: 100px;' class='text-end'><Text id='product.product-count'>Products</Text></th>
                                        <th><Text id='product.dealer'>Dealer</Text></th>
                                        <th style='width: 200px;'><Text id='common.actions'>Actions</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prefixes.length === 0 && (
                                        <tr>
                                            <td colSpan={4} class='text-center text-muted py-4'>
                                                <i class='fa-solid fa-info-circle me-2' />
                                                <Text id='product.no-prefixes'>No prefixes found. Products need names starting with 2-4 uppercase letters followed by a space.</Text>
                                            </td>
                                        </tr>
                                    )}
                                    {prefixes.map(p => (
                                        <tr key={p.prefix}>
                                            <td>
                                                <span class='badge bg-secondary font-monospace'>{p.prefix}</span>
                                            </td>
                                            <td class='text-end'>
                                                <span class='badge bg-info'>{p.productCount}</span>
                                            </td>
                                            <td>
                                                {p.dealer ? (
                                                    <span class='text-success'>
                                                        <i class='fa-solid fa-check-circle me-1' />
                                                        {p.dealer.name}
                                                    </span>
                                                ) : (
                                                    <span class='text-muted'>
                                                        <i class='fa-solid fa-exclamation-circle me-1' />
                                                        <Text id='product.not-linked'>Not linked</Text>
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {p.dealer ? (
                                                    <div class='btn-group btn-group-sm'>
                                                        <button
                                                            type='button'
                                                            class='btn btn-outline-primary'
                                                            onClick={() => this.onEditDealer(p.dealer)}
                                                            title='Edit dealer'
                                                        >
                                                            <i class='fa-solid fa-edit' />
                                                        </button>
                                                        <button
                                                            type='button'
                                                            class='btn btn-outline-danger'
                                                            onClick={() => this.onUnlinkDealer(p.prefix)}
                                                            title='Unlink dealer'
                                                        >
                                                            <i class='fa-solid fa-unlink' />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div class='btn-group btn-group-sm flex-nowrap'>
                                                        <button
                                                            type='button'
                                                            class='btn btn-outline-success text-nowrap'
                                                            onClick={() => this.onCreateDealer(p.prefix)}
                                                            title='Opprett forhandler'
                                                        >
                                                            <i class='fa-solid fa-plus me-1' />
                                                            Opprett forhandler
                                                        </button>
                                                        {dealers.length > 0 && (
                                                            <button
                                                                type='button'
                                                                class='btn btn-outline-primary text-nowrap'
                                                                onClick={() => this.onLinkPrefix(p.prefix)}
                                                                title='Link to existing dealer'
                                                            >
                                                                <i class='fa-solid fa-link me-1' />
                                                                <Text id='product.link'>Link</Text>
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* All Dealers Table */}
                        <div class='mt-4'>
                            <h6 class='mb-2'>
                                <i class='fa-solid fa-building me-2' />
                                <Text id='product.all-dealers'>All Dealers</Text> ({dealers.length})
                            </h6>
                            <div class='table-responsive'>
                                <table class={`table table-sm table-striped ${darkmode ? 'table-dark' : ''}`}>
                                    <thead>
                                        <tr>
                                            <th><Text id='product.dealer-name'>Name</Text></th>
                                            <th style='width: 80px;'><Text id='product.prefix'>Prefix</Text></th>
                                            <th><Text id='product.dealer-contact'>Contact</Text></th>
                                            <th><Text id='product.dealer-address'>Address</Text></th>
                                            <th style='width: 100px;'><Text id='common.actions'>Actions</Text></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dealers.length === 0 && (
                                            <tr>
                                                <td colSpan={5} class='text-center text-muted py-4'>
                                                    <i class='fa-solid fa-info-circle me-2' />
                                                    <Text id='product.no-dealers'>No dealers found. Click "New Dealer" to create one.</Text>
                                                </td>
                                            </tr>
                                        )}
                                        {dealers.map(d => (
                                            <tr key={d.id}>
                                                <td>
                                                    <strong>{d.name}</strong>
                                                    {d.orgNumber && <span class='text-muted ms-2 small'>({d.orgNumber})</span>}
                                                </td>
                                                <td>
                                                    {d.prefix ? (
                                                        <span class='badge bg-secondary font-monospace'>{d.prefix}</span>
                                                    ) : (
                                                        <span class='text-muted'>-</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {d.contactPerson && <div class='small'><i class='fa-solid fa-user me-1' />{d.contactPerson}</div>}
                                                    {d.phone && <div class='small'><i class='fa-solid fa-phone me-1' />{d.phone}</div>}
                                                    {d.email && <div class='small'><i class='fa-solid fa-envelope me-1' />{d.email}</div>}
                                                </td>
                                                <td>
                                                    {(d.address || d.postalcode || d.place) && (
                                                        <div class='small'>
                                                            {d.address && <span>{d.address}, </span>}
                                                            {d.postalcode && <span>{d.postalcode} </span>}
                                                            {d.place && <span>{d.place}</span>}
                                                        </div>
                                                    )}
                                                    {d.website && <div class='small'><i class='fa-solid fa-globe me-1' />{d.website}</div>}
                                                </td>
                                                <td>
                                                    <div class='btn-group btn-group-sm'>
                                                        <button
                                                            type='button'
                                                            class='btn btn-outline-primary'
                                                            onClick={() => this.onEditDealer(d)}
                                                            title='Edit dealer'
                                                        >
                                                            <i class='fa-solid fa-edit' />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
            );
        }

        // No render if neither buttonOnly nor contentOnly specified
        return null;
    }
}

export default DealersSection;
